/* oxlint-disable react/only-export-components -- compound component pattern: sub-components are intentionally bundled into a namespace object */
import { Button, ButtonProps } from '@membranehq/console/ui-kit/button'
import { css, sva } from '@membranehq/ui/styled-system/css'
import { ComponentPropsWithoutRef, PropsWithChildren, ReactNode } from 'react'
import { TbChevronLeft } from 'react-icons/tb'
import { NavLink } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

import { OpenInNewTabButton } from '../EmbedPopup/elements/OpenInNewTabButton'
import { Tooltip } from '../Tooltip'

const isolateWindowRecipe = sva({
  className: 'embed-isolate-window',
  slots: ['root', 'header', 'titleBackButton', 'content'],
  base: {
    root: {
      display: 'grid',
      gridTemplateAreas: '"header" "content"',
      gridTemplateRows: 'min-content 1fr',
      gap: '0',
      h: 'screen',
      w: 'screen',
    },
    header: {
      gridArea: 'header',
      display: 'flex',
      flexDirection: 'column',
      gap: '1',
      paddingInline: '5',
      paddingBlock: '3',
      borderBlockEndWidth: '1',
      borderBlockEndColor: 'border.subtle',
    },
    titleBackButton: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 'sm',
      paddingInlineStart: '1',
      paddingInlineEnd: '2',
      marginInlineStart: '-2',
      marginBlock: '-3',
      color: 'fg.muted',
      _hover: {
        color: 'fg.default',
      },
    },
    content: {
      gridArea: 'content',
      overflowY: 'auto',
    },
  },
})

const isolateWindowClassnames = isolateWindowRecipe()

function Window({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={twMerge(isolateWindowClassnames.root, className)} {...props}>
      <OpenInNewTabButton />
      {children}
    </div>
  )
}

function Header({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={twMerge(isolateWindowClassnames.header, className)} {...props}>
      {children}
    </div>
  )
}

function Content({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={twMerge(isolateWindowClassnames.content, className)} {...props}>
      {children}
    </div>
  )
}

function SlotWrapper({ children }: PropsWithChildren) {
  if (!children) {
    return null
  }
  return children
}

function TitleRoot({
  children,
  leftSlot,
  rightSlot,
  ...props
}: ComponentPropsWithoutRef<'h1'> & { leftSlot?: ReactNode; rightSlot?: ReactNode }) {
  if (!children && !leftSlot && !rightSlot) {
    return null
  }

  return (
    <div className='flex items-center min-h-4 pe-8'>
      <SlotWrapper>{leftSlot}</SlotWrapper>
      {children && (
        <h1 className='text-base font-medium leading-tight' {...props}>
          {children}
        </h1>
      )}
      <SlotWrapper>{rightSlot}</SlotWrapper>
    </div>
  )
}

type TitleBackButtonProps = Omit<ButtonProps, 'asChild'> & {
  tooltip?: string
}
type TitleBackButtonLinkProps = Omit<ButtonProps, 'asChild'> & {
  to: string
  tooltip?: string
}

function isTitleBackButtonLink(
  props: TitleBackButtonLinkProps | TitleBackButtonProps,
): props is TitleBackButtonLinkProps {
  return (props as TitleBackButtonLinkProps).to !== undefined
}

function TitleBackButton({ children, ...props }: TitleBackButtonLinkProps | TitleBackButtonProps) {
  if (isTitleBackButtonLink(props)) {
    const { to, ...rest } = props
    return (
      <NavLink
        to={to}
        className={css({
          display: 'flex',
        })}
      >
        <TitleBackButton {...rest}>{children}</TitleBackButton>
      </NavLink>
    )
  }

  const { tooltip, ...rest } = props

  const button = (
    <Button variant='ghost' size='dense' className={isolateWindowClassnames.titleBackButton} {...rest}>
      <TbChevronLeft className='size-5' aria-hidden />
      {children}
    </Button>
  )

  if (tooltip) {
    return <Tooltip tooltip={tooltip}>{button}</Tooltip>
  }

  return button
}

const Title = Object.assign(TitleRoot, {
  BackButton: TitleBackButton,
})

export const EmbedIsolateLayout = {
  Window,
  Header,
  Content,
  Title,
}
